import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainLayouts from "../layout/MainLayouts";
import FaqAccordion from "../components/Faq/FaqAccordion";
import useFaqs from "../components/Faq/useFaqs";

export default function FaqPage() {
  const { t } = useTranslation();
  const { faqs, categories, loading, error } = useFaqs();
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");

  // Only show category tabs that actually have questions in them.
  const usedCategories = useMemo(
    () => Object.keys(categories).filter((key) => faqs.some((f) => f.category === key)),
    [categories, faqs]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return faqs.filter((f) =>
      (activeCategory === "all" || f.category === activeCategory) &&
      (!q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q))
    );
  }, [faqs, activeCategory, search]);

  const categoryLabel = (key) => t(`faqPage.categories.${key}`, { defaultValue: categories[key] || key });

  return (
    <MainLayouts>
      {/* Hero */}
      <div className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 bg-[#0b0f19] overflow-hidden">
        <div className="relative z-10 max-w-[1000px] mx-auto px-6">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-6 flex items-center gap-3">
            <span className="block w-8 h-[2px] bg-primary"></span>
            {t("home.faq.kicker")}
          </div>
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.05] tracking-tight">
            {t("home.faq.title")}<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            {t("home.faq.subtitle")}
          </p>

          <div className="relative mt-10 max-w-xl">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("faqPage.searchPlaceholder")}
              className="w-full pl-14 pr-5 py-4 rounded-full bg-white/10 border border-white/15 text-white placeholder-gray-400 focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 py-16 lg:py-24">
        {usedCategories.length > 1 && (
          <div className="flex gap-3 overflow-x-auto pb-2 mb-10 -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
            {["all", ...usedCategories].map((key) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  activeCategory === key
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-[#0b0f19] hover:bg-gray-200"
                }`}
              >
                {key === "all" ? t("faqPage.all") : categoryLabel(key)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-5">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="h-[88px] rounded-[10px] border border-gray-100 bg-gray-50 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-gray-500 py-16">{t("faqPage.loadError")}</p>
        ) : visible.length > 0 ? (
          // key resets which item is open when the filter changes
          <FaqAccordion key={`${activeCategory}|${search}`} faqs={visible} defaultOpen={search ? 0 : null} />
        ) : (
          <p className="text-center text-gray-500 py-16">{t("faqPage.noResults")}</p>
        )}

        {/* Still have questions */}
        <div className="mt-20 rounded-[24px] bg-gray-50 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0b0f19] mb-2">{t("faqPage.stillTitle")}</h2>
            <p className="text-gray-500 text-lg">{t("faqPage.stillText")}</p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 px-8 py-4 rounded-full bg-primary hover:bg-[#b04233] text-white font-bold transition-colors"
          >
            {t("faqPage.contactUs")}
          </Link>
        </div>
      </div>
    </MainLayouts>
  );
}
